import { useState } from 'react';
import {
  Box,
  Button,
  List,
  ListItemButton,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  InputAdornment,
  Tooltip
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SearchIcon from '@mui/icons-material/Search';

export default function ChatSidebar({
  chats = [],
  selectedChatId,
  onNewChat = () => {},
  onSelectChat = () => {},
  onDeleteChat,
  onRenameChat
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuChatId, setMenuChatId] = useState(null);
  const [hoveredChatId, setHoveredChatId] = useState(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [actionChatId, setActionChatId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const open = Boolean(anchorEl);

  const handleMenuOpen = (event, chatId) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget.closest('[data-chat-item]'));
    setMenuChatId(chatId);
    setActionChatId(chatId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const cleanTitle = (title) =>
    title?.replace(/^#+\s*/, '').trim() || 'New chat';

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  const filteredChats = chats.filter((chat) => {
    if (!searchQuery) return true;
    return cleanTitle(chat.title).toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <>
      <Box sx={{ width: 300, borderRight: '1px solid', borderColor: 'divider', p: 2, height: '100vh', overflowY: 'auto' }}>
        {/* CREATE NEW CHAT */}
        <Button fullWidth variant="outlined" onClick={onNewChat} sx={{ mb: 1.5 }}>
          + New Chat
        </Button>

        {/* SEARCH */}
        <TextField
          size="small"
          fullWidth
          placeholder="Search chats..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            )
          }}
        />

        {/* CHAT LIST */}
        <List sx={{ mt: 1 }}>
          {filteredChats.map((chat) => (
            <ListItemButton
              data-chat-item
              key={chat.id}
              onMouseEnter={() => setHoveredChatId(chat.id)}
              onMouseLeave={() => setHoveredChatId(null)}
              selected={chat.id === selectedChatId}
              onClick={() => onSelectChat(chat.id)}
              sx={{ borderRadius: 1.5, mb: 0.5, py: 1, px: 1.5 }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Tooltip title={cleanTitle(chat.title)} placement="right" enterDelay={600}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'block',
                      fontWeight: chat.id === selectedChatId ? 600 : 400
                    }}
                  >
                    {cleanTitle(chat.title)}
                  </Typography>
                </Tooltip>
                {chat.updated_at && (
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
                    {timeAgo(chat.updated_at || chat.created_at)}
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  width: 32,
                  display: 'flex',
                  justifyContent: 'center',
                  opacity: hoveredChatId === chat.id ? 1 : 0,
                  pointerEvents: hoveredChatId === chat.id ? 'auto' : 'none',
                  transition: 'opacity 0.15s ease',
                  flexShrink: 0
                }}
              >
                <IconButton size="small" onClick={(e) => handleMenuOpen(e, chat.id)}>
                  <MoreHorizIcon fontSize="small" />
                </IconButton>
              </Box>
            </ListItemButton>
          ))}
          {filteredChats.length === 0 && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.disabled">
                {searchQuery ? 'No chats match your search' : 'No chats yet'}
              </Typography>
            </Box>
          )}
        </List>

        {/* ✅ MENU */}
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
          transformOrigin={{ vertical: 'center', horizontal: 'right' }}
          PaperProps={{
            sx: {
              borderRadius: 1.5,
              boxShadow: '0 8px 30px rgba(0,0,0,0.15)'
            }
          }}
        >
          <MenuItem
            disabled={!onRenameChat}
            onClick={() => {
              const chat = chats.find((c) => c.id === menuChatId);
              setRenameValue(chat?.title || 'New Chat');
              setRenameOpen(true);
              handleMenuClose();
            }}
            sx={{
              fontSize: 14,
              '&:hover': { bgcolor: '#f5f5f5' }
            }}
          >
            <EditOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
            Rename
          </MenuItem>

          <MenuItem
            disabled={!onDeleteChat}
            onClick={() => {
              setDeleteOpen(true);
              handleMenuClose();
            }}
            sx={{
              fontSize: 14,
              color: 'error.main',
              '&:hover': { bgcolor: '#fdecea' }
            }}
          >
            <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        </Menu>
      </Box>
      <Dialog open={renameOpen} onClose={() => setRenameOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Rename chat</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth size="small" value={renameValue} onChange={(e) => setRenameValue(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              onRenameChat?.(actionChatId, renameValue);
              setRenameOpen(false);
              setMenuChatId(null);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete chat?</DialogTitle>
        <DialogContent>This will permanently delete the chat and its messages.</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              onDeleteChat?.(actionChatId);
              setDeleteOpen(false);
              setMenuChatId(null);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
